import { TextField } from '@doordash/component-fields'
import Separator from '@doordash/component-separator'
import Tag from '@doordash/component-tag'
import Toggle from '@doordash/component-toggle'
import {
  Colors,
  InlineChildren,
  Inset,
  Stack,
  StackChildren,
  Text,
} from '@doordash/design-language'
import { Interpolate } from '@doordash/lib-i18n'
import { noop } from 'lodash'
import * as React from 'react'
import { MutableRefObject } from 'react'
import { connect } from 'react-redux'
import { i18nKeys } from 'translationTypes'
import {
  BASE_GROWTH_IMAGE_CDN,
  DASHER_APPLICANT_FAQ_URL,
} from '../../../../constants'
import i18n from '../../../../i18n'
import { Application } from '../../../../utilities/application/application'
import Form from '../../../../utilities/Form'
import { validators as sharedValidators } from '../../../../utilities/validators'
import { InfoTextField } from '../../../atoms/info-text-field'
import { FormRefProps } from '../config'

const nccCheckIdImageUrl = `${BASE_GROWTH_IMAGE_CDN}/background-check/ncc-check-id.png`

interface EmailInputProps {
  email: string
  isNccEmailDifferent: boolean
  onToggle: (val: boolean) => void
  onChange: (val: string) => void
  value: string
}

interface CheckIdInputProps {
  email: string
  onChange: (val: string) => void
  value: string
}

interface NccShareReviewProps {
  formRef: MutableRefObject<FormRefProps>
  application: Application
}

interface PersonalDetailsProps {
  application: Application
}

const PersonalDetails: React.FC<PersonalDetailsProps> = ({ application }) => {
  const {
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    phoneNumber,
  } = application

  return (
    <StackChildren size={StackChildren.Sizes.Large}>
      <Text styles={Text.Styles.Title2}>
        {i18n.t(
          i18nKeys.backgroundCheckShare__nccReview__nccPersonalDetailsSectionHeader
        )}
      </Text>
      <Text styles={Text.Styles.Body1} color={Colors.TextSecondary}>
        <Interpolate
          i18n={i18n}
          i18nKey={
            i18nKeys.backgroundCheckShare__nccReview__nccPersonalDetailsChangeNeeded
          }
          components={[
            <Text
              display={Text.DisplayModes.Inline}
              tag="a"
              target="_blank"
              href={DASHER_APPLICANT_FAQ_URL}
            />,
          ]}
        />
      </Text>
      <InfoTextField
        label={i18n.t(i18nKeys.common__legalFirstName)}
        value={firstName}
      />
      {middleName && (
        <InfoTextField
          label={i18n.t(i18nKeys.common__legalMiddleName)}
          value={middleName}
        />
      )}
      <InfoTextField
        label={i18n.t(i18nKeys.common__legalLastName)}
        value={lastName}
      />
      <InfoTextField
        label={i18n.t(i18nKeys.common__birthdate)}
        value={dateOfBirth}
      />
      <InfoTextField
        label={i18n.t(i18nKeys.common__mobile)}
        value={phoneNumber}
      />
    </StackChildren>
  )
}

const EmailInput: React.FC<EmailInputProps> = ({
  email: ddEmail,
  isNccEmailDifferent,
  onToggle,
  onChange,
  value,
  ...rest
}) => {
  return (
    <StackChildren size={StackChildren.Sizes.Large}>
      <StackChildren size={StackChildren.Sizes.XxSmall}>
        <Text styles={Text.Styles.Title2}>
          {i18n.t(i18nKeys.backgroundCheckShare__nccReview__ddEmailLabel)}
        </Text>
        <Text styles={Text.Styles.Body1}>{ddEmail}</Text>
      </StackChildren>
      <Toggle
        label={i18n.t(
          i18nKeys.backgroundCheckShare__nccReview__isNccEmailDifferentLabel
        )}
        renderLabel={({ htmlFor }) => (
          <Text color={Colors.TextSecondary} htmlFor={htmlFor}>
            {i18n.t(
              i18nKeys.backgroundCheckShare__nccReview__isNccEmailDifferentLabel
            )}
          </Text>
        )}
        isSelected={isNccEmailDifferent}
        onChange={() => {
          onToggle(!isNccEmailDifferent)
          onChange('')
        }}
      />
      {isNccEmailDifferent && (
        <TextField
          label={i18n.t(
            i18nKeys.backgroundCheckShare__nccReview__nccEmailLabel
          )}
          placeholder={i18n.t(
            i18nKeys.backgroundCheckShare__nccReview__nccEmailPlaceholder
          )}
          onChange={onChange}
          value={value}
          {...rest}
        />
      )}
    </StackChildren>
  )
}

const CheckIdInput: React.FC<CheckIdInputProps> = ({
  value,
  onChange,
  ...rest
}) => {
  return (
    <StackChildren size={StackChildren.Sizes.Large}>
      <InlineChildren
        justifyContent={InlineChildren.Justification.SpaceBetween}
        alignItems={InlineChildren.Alignment.Center}
      >
        <Text styles={Text.Styles.Title2}>
          {i18n.t(
            i18nKeys.backgroundCheckShare__nccReview__nccCheckIdSectionHeader
          )}
        </Text>
        <Tag size={Tag.Sizes.Medium} text={i18n.t(i18nKeys.common__optional)} />
      </InlineChildren>
      <Text styles={Text.Styles.Body1} color={Colors.TextSecondary}>
        {i18n.t(
          i18nKeys.backgroundCheckShare__nccReview__nccCheckIdInputDescription
        )}
      </Text>
      <TextField
        label={i18n.t(
          i18nKeys.backgroundCheckShare__nccReview__nccCheckIdInputLabel
        )}
        placeholder={i18n.t(
          i18nKeys.backgroundCheckShare__nccReview__nccCheckIdInputPlaceholder
        )}
        value={value}
        onChange={onChange}
        {...rest}
      />
      <StackChildren size={StackChildren.Sizes.Small}>
        <Text styles={Text.Styles.FieldLargeLabel}>
          {i18n.t(
            i18nKeys.backgroundCheckShare__nccReview__nccCheckIdWhereToFindLabel
          )}
        </Text>
        <img
          src={nccCheckIdImageUrl}
          alt={i18n.t(
            i18nKeys.backgroundCheckShare__nccReview__nccCheckIdWhereToFindAltText
          )}
          width="100%"
        />
        <Text styles={Text.Styles.Body1} color={Colors.TextSecondary}>
          {i18n.t(
            i18nKeys.backgroundCheckShare__nccReview__nccCheckIdWhereToFindDescription
          )}
        </Text>
      </StackChildren>
    </StackChildren>
  )
}

const NccShareReview: React.FC<NccShareReviewProps> = ({
  formRef,
  application,
}) => {
  const [isNccEmailDifferent, setIsNccEmailDifferent] = React.useState(false)
  const { email: ddEmail } = application
  const fields = {
    nccEmail: '',
    nccCheckId: '',
  }

  const validators = {
    nccEmail: isNccEmailDifferent ? sharedValidators.email : noop,
    // TODO @sujay.gandham: add NCC check ID validation
    nccCheckId: noop,
  }

  const submit = async data => {
    if (data) {
      // TODO @sujay-gandham-dd: pass form fields to NCC check share API once ready
      formRef.current.isValid = true
    }
  }

  return (
    <Inset verticalSize={Inset.Sizes.Small} horizontalSize={Inset.Sizes.None}>
      <StackChildren size={Stack.Sizes.Large}>
        <Text styles={Text.Styles.Body1} color={Colors.TextSecondary}>
          {i18n.t(i18nKeys.backgroundCheckShare__nccReview__subtitle)}
        </Text>
        <Form onSubmit={submit} validators={validators} fields={fields}>
          {(fields, handleChange, handleSubmit) => (
            <form
              onSubmit={handleSubmit}
              ref={el => (formRef.current.form = el)}
            >
              <EmailInput
                email={ddEmail}
                isNccEmailDifferent={isNccEmailDifferent}
                onToggle={setIsNccEmailDifferent}
                onChange={val => handleChange('nccEmail', val)}
                {...fields.nccEmail}
              />
              <Separator
                verticalMargin={Separator.Sizes.Large}
                type={Separator.Types.FullBorder}
              />
              <PersonalDetails application={application} />
              <Separator
                verticalMargin={Separator.Sizes.Large}
                type={Separator.Types.FullBorder}
              />
              <CheckIdInput
                onChange={val => handleChange('nccCheckId', val)}
                {...fields.nccCheckId}
              />
            </form>
          )}
        </Form>
      </StackChildren>
    </Inset>
  )
}

const mapStateToProps = state => ({
  application: state.application,
})

export default connect(mapStateToProps)(NccShareReview)
